import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { useFormController } from '@dreryk/react-common';
import { FormContent } from '@dreryk/react-components';

import { CitoCheckbox } from '../../shared/CitoCheckbox';
import { resolveDiagnosesFormPart } from '../../shared/common.utils';
import { DiagnosesWithBodySideInteractiveTable } from '../../shared/DiagnosesWithBodySideInteractiveTable';
import { ExaminationsPerformedTextarea } from '../../shared/ExaminationsPerformedTextarea';
import { ObjectiveTextarea } from '../../shared/ObjectiveTextarea';
import { StyledReferralForm } from '../../shared/StyledReferralForm';
import { referralTranslationKeys } from '../../translations';
import { Icd9ProcedureSelector } from '../../shared/Icd9ProcedureField/Icd9ProcedureSelector';
import { HealthcareFacilityLocationSpecialtyField } from '../../shared/HealthcareFacilitySpecialtyField/HealthcareFacilityLocationSpecialtyField';

import {
   ReferralToDiagnosticExaminationFormBean,
   ReferralToDiagnosticExaminationFormController,
} from './ReferralToDiagnosticExaminationForm.types';

const getInitialValues = (
   referralToDiagnosticExaminationDraft: ReferralToDiagnosticExaminationFormBean | null,
   visitDiagnoses?: VisitDiagnosis[]
): ReferralToDiagnosticExaminationFormBean => {
   return {
      cito: referralToDiagnosticExaminationDraft?.cito ?? false,
      diagnoses: resolveDiagnosesFormPart(referralToDiagnosticExaminationDraft?.diagnoses, visitDiagnoses),
      icd9Procedures: referralToDiagnosticExaminationDraft?.icd9Procedures ?? [],
      healthcareFacilityLocationSpecialty:
         referralToDiagnosticExaminationDraft?.healthcareFacilityLocationSpecialty ?? null,
      objective: referralToDiagnosticExaminationDraft?.objective ?? null,
      examinationsAlreadyPerformed: referralToDiagnosticExaminationDraft?.examinationsAlreadyPerformed ?? null,
   };
};

type ReferralToDiagnosticExaminationFormChildrenRenderProps = Pick<
   ReferralToDiagnosticExaminationFormController,
   'handleSubmit' | 'handleSubmitDraft' | 'formState' | 'reset' | 'trigger'
>;

type ReferralToDiagnosticExaminationFormProps = {
   readonly visitDiagnoses: VisitDiagnosis[];
   readonly visitBeginDate: Date;
   readonly referralToDiagnosticExaminationDraft: ReferralToDiagnosticExaminationFormBean | null;
   readonly children: (renderProps: ReferralToDiagnosticExaminationFormChildrenRenderProps) => JSX.Element;
};

export const ReferralToDiagnosticExaminationForm = (props: ReferralToDiagnosticExaminationFormProps): JSX.Element => {
   const {
      register,
      formState,
      control,
      handleSubmit,
      handleSubmitDraft,
      reset,
      trigger,
   } = useFormController<ReferralToDiagnosticExaminationFormBean>(
      getInitialValues(props.referralToDiagnosticExaminationDraft, props.visitDiagnoses)
   );

   return (
      <StyledReferralForm onSubmit={(event) => event.preventDefault()} noValidate>
         <FormContent testId={'diagnosticExamination'}>
            <CitoCheckbox register={register} />
            <HealthcareFacilityLocationSpecialtyField specialtyType={'diagnosticLaboratory'} register={register} />
            <DiagnosesWithBodySideInteractiveTable
               register={register}
               visitDiagnoses={props.visitDiagnoses}
               dictionaryValidOn={props.visitBeginDate}
               required
            />
            <Icd9ProcedureSelector
               control={control}
               dictionaryValidOn={props.visitBeginDate}
               defaultValues={props.referralToDiagnosticExaminationDraft?.icd9Procedures}
            />
            <ObjectiveTextarea
               register={register}
               required={false}
               label={referralTranslationKeys.examinationObjective}
               placeholder={referralTranslationKeys.insertExaminationObjective}
            />
            <ExaminationsPerformedTextarea register={register} />
            {props.children({
               handleSubmit: handleSubmit,
               handleSubmitDraft: handleSubmitDraft,
               formState: formState,
               reset: reset,
               trigger: trigger,
            })}
         </FormContent>
      </StyledReferralForm>
   );
};
